import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
  delay?: number;
}

export function useScrollAnimation(options: UseScrollAnimationOptions = {}) {
  const { threshold = 0.12, rootMargin = '0px 0px -50px 0px', once = true } = options;
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}

export function useStaggerAnimation(count: number, options: UseScrollAnimationOptions = {}) {
  const { threshold = 0.08, rootMargin = '0px 0px -30px 0px', once = true } = options;
  const ref = useRef<HTMLElement>(null);
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(count).fill(false));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          for (let i = 0; i < count; i++) {
            setTimeout(() => {
              setVisibleItems((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 120);
          }
          if (once) observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [count, threshold, rootMargin, once]);

  return { ref, visibleItems };
}

// Utility: CSS class string for scroll-in animation
export function scrollInClass(isVisible: boolean, direction: 'up' | 'down' | 'left' | 'right' | 'scale' = 'up', delay = 0): string {
  const base = 'transition-all ease-out';
  const dur = 'duration-700';
  const delayClass = delay > 0 ? `delay-[${delay}ms]` : '';

  if (!isVisible) {
    const hidden: Record<string, string> = {
      up: 'opacity-0 translate-y-12',
      down: 'opacity-0 -translate-y-12',
      left: 'opacity-0 translate-x-12',
      right: 'opacity-0 -translate-x-12',
      scale: 'opacity-0 scale-90',
    };
    return `${base} ${dur} ${delayClass} ${hidden[direction]}`;
  }
  return `${base} ${dur} ${delayClass} opacity-100 translate-y-0 translate-x-0 scale-100`;
}
